import React, { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useEmbeddedCustomerAgreementsQuery } from 'hooks/queries/useEmbeddedCustomerQuery'
import { AgreementNames } from 'pages/Verification/Review/helpers/constants'
import { AgreementsForm } from './components/AgreementsForm'
import { EmbeddedWrapper } from './components/EmbeddedWrapper'
import { PageSkeletonLoader } from './components/PageSkeletonLoader'
import { SessionStatusView } from './components/SessionStatusView'
import {
  EmbeddedAgreementStatuses,
  PARAMS,
  SessionStatusCodes,
  SessionStatusMapping,
} from './helpers/constants'
import { EmbeddedAgreementStatus } from './helpers/types'
import { isValidHexCode, toBoolean } from './helpers/utils'

export const EmbeddedCustomerTerms = () => {
  const [searchParams] = useSearchParams()
  const [sessionStatus, setSessionStatus] = useState<EmbeddedAgreementStatus>()
  const reference = searchParams?.get(PARAMS.REFERENCE)
  const redirectUri = searchParams?.get(PARAMS.REDIRECT)
  const primaryColor = searchParams?.get(PARAMS.PRIMARY_COLOR)
  const isDarkMode = toBoolean(searchParams?.get(PARAMS.IS_DARK_MODE), false)
  const isBranded = toBoolean(searchParams?.get(PARAMS.IS_BRANDED), true)

  const uiConfig = {
    primaryColor: isValidHexCode(primaryColor),
    isDarkMode,
    isBranded,
  }

  const {
    data: { agreements = [], status } = {},
    isFetching,
    isError,
  } = useEmbeddedCustomerAgreementsQuery(reference)

  const pendingAgreements = (agreements || [])?.filter(
    ({ status }) => status === EmbeddedAgreementStatuses.PENDING
  )

  const isUsEntity = useMemo(
    () => pendingAgreements?.some(({ name }) => name === AgreementNames.MASTER_CLIENT_AGREEMENT_US),
    [pendingAgreements]
  )

  const mappedSessionStatus = useMemo(() => SessionStatusMapping[sessionStatus], [sessionStatus])

  const onSuccess = () => {
    setSessionStatus(EmbeddedAgreementStatuses.SIGNED)
  }

  useEffect(() => {
    if (!isFetching && status) {
      setSessionStatus(status)
    }
  }, [isFetching, status])

  const StatusView = useMemo(() => {
    if (!reference) {
      return <SessionStatusView statusCode={SessionStatusCodes.NO_REFERENCE} uiConfig={uiConfig} />
    }
    if (isFetching) {
      return <PageSkeletonLoader showCancel={!!redirectUri} />
    }
    if (isError) {
      return <SessionStatusView statusCode={SessionStatusCodes.QUERY_ERROR} uiConfig={uiConfig} />
    }
    if (mappedSessionStatus) {
      return <SessionStatusView statusCode={mappedSessionStatus} uiConfig={uiConfig} />
    }
    if (!pendingAgreements?.length) {
      return <SessionStatusView statusCode={SessionStatusCodes.NO_AGREEMENTS} uiConfig={uiConfig} />
    }
  }, [reference, isFetching, isError, mappedSessionStatus, pendingAgreements?.length, redirectUri])

  return (
    <EmbeddedWrapper uiConfig={uiConfig} isUsEntity={isUsEntity}>
      {StatusView || (
        <AgreementsForm
          reference={reference}
          redirectUri={redirectUri}
          isUsEntity={isUsEntity}
          agreements={pendingAgreements}
          uiConfig={uiConfig}
          onSuccess={onSuccess}
        />
      )}
    </EmbeddedWrapper>
  )
}
